import { useMutation, useQuery } from "@tanstack/react-query";
import {
  dismissNotification,
  dismissNotifications,
  getNotifications,
  feedbackConversationAuthorityFingerprint,
  markNotificationsRead,
} from "@/lib/api";
import { queryClient, useScopedKey } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import type { NotificationsPayload } from "@/lib/types";

function invalidateNotifications() {
  void queryClient.invalidateQueries({
    predicate: (q) => (q.queryKey as unknown[])[0] === "notifications",
  });
}

export function useNotifications() {
  const signedIn = useAuthStore((s) => !!s.user);
  const actorId = useAuthStore(s=>s.user?.id);
  const queryKey = useScopedKey("notifications", { actorId, authority: feedbackConversationAuthorityFingerprint() });
  const query = useQuery<NotificationsPayload>({
    queryKey,
    queryFn: getNotifications,
    enabled: signedIn,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const readMutation = useMutation({
    mutationFn: (body: { keys?: string[]; all_current?: boolean }) =>
      markNotificationsRead(body),
    onSuccess: invalidateNotifications,
  });
  const dismissMutation = useMutation({
    // Phase C: a grouped row dismisses every member key at once. A
    // single string keeps the single-key endpoint; an array uses the
    // bulk `{ keys }` path the backend already accepts.
    mutationFn: (key: string | string[]) =>
      Array.isArray(key)
        ? dismissNotifications({ keys: key })
        : dismissNotification(key),
    onSuccess: invalidateNotifications,
  });
  const dismissAllMutation = useMutation({
    mutationFn: () => dismissNotifications({ all_current: true }),
    onSuccess: invalidateNotifications,
  });

  return {
    ...query,
    data: signedIn && !query.error ? query.data : undefined,
    markRead: (keys: string[]) => readMutation.mutateAsync({ keys }),
    markAllRead: () => readMutation.mutateAsync({ all_current: true }),
    dismiss: (key: string | string[]) => dismissMutation.mutateAsync(key),
    clearAll: () => dismissAllMutation.mutateAsync(),
    isMutating:
      readMutation.isPending ||
      dismissMutation.isPending ||
      dismissAllMutation.isPending,
  };
}
